import { landingFunnelKey } from "@/constants/funnelRouteDeclareValues";
import useFunnelRouteDeclare from "@/hooks/useFunnelRouteDeclare";
import useLandingPageEffect from "@/hooks/useLandingPage";

import {
  BaseMobileLayout,
  colors,
  customEvent,
  getCdnImageSrc,
  kakaoPixel,
  pixelCustomEvent,
  PixelEventName,
  RenewalFooter,
  Spacing,
  tossPixel,
  VLine,
} from "goi_common";
import { BaseStack } from "goi_common/src/design-system";
import GlobalCollaborationSection from "goi_common/src/components/Prime/GlobalCollaborationSection";

import { Box } from "@chakra-ui/react";

import {
  HomeV4BenefitSection,
  HomeV4HeroSection,
  HomeV4ReviewSection,
  HomeV4GoiIntroSection,
  HomeV4SafeSection,
  HomeV4PriceSection,
  HomeV4CTASection,
  HomeV4YoutubeSection,
  HomeV4PRSection,
} from "@/containers/HomeV4/components";

import { naverConversionEvent } from "@/utils/naverPageView";

import FixedSubmitButton from "@/components/common/FixedSubmitButton";
import useLandingPageScroll from "@/hooks/useLandingPageScroll";
import { useRouter } from "next/router";
import { PRODUCT_GROUP } from "@/constants/productKey";
import { useCustomFeature } from "@/hooks/useCustomFeature";

export default function Promotion26A() {
  const router = useRouter();
  const { paths } = useFunnelRouteDeclare({
    uniqueKey: landingFunnelKey.GROUP["promotion_26_d"],
  });

  const feature = useCustomFeature(24);

  useLandingPageEffect();

  useLandingPageScroll({
    verificationHref: `${paths.verification}`,
  });

  const handleCtaClick = (
    trigger_and_target: "fixed_CTA" | "hero_CTA-uv_registration" | "section_CTA"
  ) => {
    customEvent({
      newGtm: {
        path: "home",
        trigger_and_target: "CTA",
      },
    });

    customEvent({
      newGtm: {
        path: "home",
        trigger_and_target,
      },
    });

    customEvent({
      newGtm: {
        path: "promotion",
        trigger_and_target: "CTA",
      },
    });

    customEvent({
      newGtm: {
        path: "promotion",
        trigger_and_target,
      },
    });

    naverConversionEvent({ type: "view_product" });
    kakaoPixel()?.participation();
    tossPixel()?.productView();
    pixelCustomEvent(PixelEventName.ViewContent);

    router.push(
      paths["product-intro"] ||
        `/registration/product-intro/?productGroup=${PRODUCT_GROUP["9900"]}`
    );
  };

  return (
    <BaseMobileLayout padding="0px">
      <HomeV4HeroSection
        title={`설 연휴 가입 특전\n5만원 상품권 100명 추첨`}
        description={`사랑하는 가족 위해 상조 가입하고\n역대 최대 규모 혜택도 받아가세요!`}
        imageSrc={getCdnImageSrc("direct/home/shinsegae-50000.png")}
        buttonText="시작하기"
        onClick={() => {
          handleCtaClick("hero_CTA-uv_registration");
        }}
        disabled={!feature}
      />

      <DescriptionSection />
      <HomeV4BenefitSection />
      <HomeV4SafeSection />
      <HomeV4PriceSection
        title="타사 대비 절반의 비용으로 준비하세요"
        description={
          <>
            월 납입금, 총 상품 금액 모두
            <br />
            타사 대비 절반 수준으로 낮췄습니다.
          </>
        }
        imageSrc="images/package-price/289/prime/table-price-9900.png"
      />
      <HomeV4ReviewSection />
      <HomeV4YoutubeSection />
      <VLine width="100%" height="16px" color={colors.gray50} />
      <HomeV4GoiIntroSection />

      <GlobalCollaborationSection
        top={
          <>
            <Box className="body_16_r gray700">믿을 수 있는 파트너</Box>
            <Spacing gap={8} />
            신뢰도 높은 기관들과 함께
            <br />
            상조 서비스를 혁신하고 있습니다
          </>
        }
      />
      <HomeV4PRSection />

      <HomeV4CTASection
        onClick={() => {
          handleCtaClick("section_CTA");
        }}
        disabled={!feature}
      />
      <VLine width="100%" height="1px" color={colors.gray200} />
      <RenewalFooter />

      <FixedSubmitButton
        onClick={() => {
          handleCtaClick("fixed_CTA");
        }}
        disabled={!feature}
      >
        고이와 함께 장례 준비 시작하기
      </FixedSubmitButton>
    </BaseMobileLayout>
  );
}

function DescriptionSection() {
  return (
    <BaseStack width="100%" padding="45px 20px 0px" alignItems="center">
      <Box
        css={{
          fontSize: "24px",
          fontWeight: 600,
          lineHeight: "32px",
          letterSpacing: "-0.3px",
          color: colors.gray900,
        }}
      >
        이벤트 <span css={{ color: colors.orange650 }}>필수 안내사항</span>
      </Box>

      <Spacing gap={24} />

      <BaseStack width="100%" spacing={11} px={4}>
        <BaseStack
          direction="horizontal"
          css={{
            fontSize: "16px",
            lineHeight: "24px",
            color: colors.gray900,
          }}
        >
          <Box css={{ fontWeight: 600, width: "80px", flexShrink: 0 }}>
            참여 기간
          </Box>
          <Box>2/12~2/19</Box>
        </BaseStack>
        <BaseStack
          direction="horizontal"
          css={{
            fontSize: "16px",
            lineHeight: "24px",
            color: colors.gray900,
          }}
        >
          <Box css={{ fontWeight: 600, width: "80px", flexShrink: 0 }}>
            경품 내용
          </Box>
          <Box>백화점 상품권 5만원(100명 추첨)</Box>
        </BaseStack>
        <BaseStack
          direction="horizontal"
          css={{
            fontSize: "16px",
            lineHeight: "24px",
            color: colors.gray900,
          }}
        >
          <Box css={{ fontWeight: 600, width: "80px", flexShrink: 0 }}>
            추첨 대상
          </Box>
          <Box>본 링크로 9900원 상품 가입한 고객</Box>
        </BaseStack>
        <BaseStack
          direction="horizontal"
          css={{
            fontSize: "16px",
            lineHeight: "24px",
            color: colors.gray900,
          }}
        >
          <Box css={{ fontWeight: 600, width: "80px", flexShrink: 0 }}>
            지급 날짜
          </Box>
          <Box>3/31 당첨자 한정 문자 개별 발송</Box>
        </BaseStack>
      </BaseStack>

      <Spacing gap={19} />

      <Box
        css={{
          padding: "16px",
          background: colors.gray50,
          borderRadius: "12px",
          width: "100%",
        }}
      >
        <Box
          className="caption_12_r gray700"
          css={{
            lineHeight: "18px",
          }}
        >
          • 이벤트는 이벤트 기간 내 고이 9900원 상조 가입을 진행한 대상으로
          진행하며, 본 이벤트의 경품인 백화점상품권 5만원의 지급은 무작위 추첨
          방식으로 진행됩니다.
          <br />
          <br />• 경품 지급을 위해서는 이벤트 참여 시 마케팅 정보 수신 동의가
          필수이며, 동의하지 않으신 경우 추첨 대상에서 제외됩니다.
          <br />
          <br />• 경품 당첨 결과는 당첨자 한정으로 고이 가입 시 등록된
          연락처(문자 메시지 또는 푸시 알림 등)를 통해 개별적으로 안내될
          예정입니다. 회원정보오류, 미등록, 회원탈퇴 등으로 인해 회원정보의
          확인이 불가능하거나 연락이 되지 않을 경우, 별도의 고지없이 당첨이
          취소될 수 있습니다.
          <br />
          <br />• 당사 및 제휴사의 사정에 의해 사전 고지 없이 이벤트의 내용이
          변경되거나 조기 종료될 수 있습니다.
        </Box>
      </Box>
    </BaseStack>
  );
}
